import React from "react";
import { connect } from "react-redux";
import { PRODUCT_PREVIEW_SIGN_UP } from "web/react/components/product-preview-sign-up/product-preview-sign-up";
import WebToAppCampaign from "web/react/components/web-to-app-campaign/web-to-app-campaign";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import {
    getAnalyticsEventLabel,
    useSaveForLater,
} from "web/react/hooks/use-save-for-later/use-save-for-later";
import withReduxProvider from "web/react/redux-provider";
import { CaptureType, openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import history from "web/script/utils/history";
import { OneOrAnother } from "web/types/utils";
import SaveForLater from "./save-for-later";

// In case we want to show PDP Preview Sign Up Modal, instead of Customer Capture Overlay,
// then we need to pass both `linkId` and  `showPdpSignUp` prop.
// eslint-disable-next-line @typescript-eslint/ban-types
type PdpSignUpProps = OneOrAnother<{ linkId: string; showPdpSignUp: boolean }, object>;

export type PageSource = "pdp" | "feed" | "oos";
export type ButtonType =
    | "cta"
    | "icon"
    | "LEGACY_normal"
    | "LEGACY_small"
    | "LEGACY_stockAlert"
    | "LEGACY_tick";

interface BasePropsInterface {
    captureType?: CaptureType;
    className?: string;
    cta?: string;
    pageSource: PageSource;
    productId: string;
    type?: ButtonType;
    variant?: "emotional";
    showExpressCheckout?: boolean;
    bingTraffic?: boolean;
    productImageURL?: string;
    isAffiliate?: boolean;
    isCheckout?: boolean;
    appDeeplinkPath?: string;
    analyticsEventLabel?: string;
}

type BaseProps = BasePropsInterface & PdpSignUpProps;

type AnonymousSFLButtonProps = BaseProps & {
    openCustomerCaptureOverlay: (object) => void;
};

export function AnonymousSFLButton({
    captureType,
    className,
    cta,
    openCustomerCaptureOverlay,
    pageSource,
    productId,
    linkId,
    showPdpSignUp,
    type = "LEGACY_normal",
    showExpressCheckout,
    bingTraffic,
    variant,
    productImageURL,
    isAffiliate,
    isCheckout,
    appDeeplinkPath,
    analyticsEventLabel,
}: AnonymousSFLButtonProps): JSX.Element {
    const trackClick = useClickTracker(
        "save_for_later",
        "add_item",
        getAnalyticsEventLabel(pageSource),
        false
    );

    const membershipAnalyticsEventLabel = analyticsEventLabel || "s4l_app_signup";

    function handleClick(): void {
        if (showPdpSignUp) {
            return history.pushOverlay(PRODUCT_PREVIEW_SIGN_UP, {
                data: {
                    linkId,
                },
            });
        } else {
            openCustomerCaptureOverlay({
                productId: productId,
                productImageUrl: productImageURL,
                captureType:
                    captureType ||
                    (isCheckout ? "signup_checkout_save" : `signup_${pageSource}_save`), // pageSource = 'pdp' or 'feed'
                appDeeplinkPath: appDeeplinkPath,
                analyticsEventLabel: membershipAnalyticsEventLabel,
            });
        }
    }

    return (
        <WebToAppCampaign
            captureType={captureType}
            contextType={"save_for_later"}
            defaultAction={handleClick}
            options={{
                isAffiliate,
                isIcon: isCheckout,
            }}
            beforeOnClick={(event) => trackClick(event)}
            analyticsEventLabel={membershipAnalyticsEventLabel}
            appDeepLinkPath={appDeeplinkPath}
            productImageUrl={productImageURL}
            pageSource={pageSource}
            productId={productId}
        >
            <SaveForLater
                className={className}
                cta={cta}
                isSaved={false}
                type={type}
                variant={variant}
                showExpressCheckout={showExpressCheckout}
                bingTraffic={bingTraffic}
            />
        </WebToAppCampaign>
    );
}

const _AnonymousSFLButton = withReduxProvider(
    connect(null, { openCustomerCaptureOverlay })(AnonymousSFLButton)
);

type AuthenticatedSFLButtonProps = BaseProps & {
    initialIsSaved?: boolean;
};

export function AuthenticatedSFLButton({
    className,
    cta,
    initialIsSaved,
    pageSource,
    productId,
    variant,
    type = "cta",
    showExpressCheckout,
    bingTraffic,
}: AuthenticatedSFLButtonProps): JSX.Element {
    const [_isSaved, , toggleSaved, isSaving] = useSaveForLater(
        productId,
        pageSource,
        initialIsSaved
    );

    const isSaved = Boolean(_isSaved);

    return (
        <span>
            <SaveForLater
                className={className}
                cta={cta}
                variant={variant}
                isSaved={isSaved}
                isSaving={isSaving}
                onClick={toggleSaved}
                type={type}
                showExpressCheckout={showExpressCheckout}
                bingTraffic={bingTraffic}
            />
        </span>
    );
}

type SaveForLaterButtonProps = BaseProps & {
    isLoggedIn: boolean;
};

function SaveForLaterButton({ isLoggedIn, ...props }: SaveForLaterButtonProps): JSX.Element {
    if (isLoggedIn) {
        return <AuthenticatedSFLButton {...props} />;
    } else {
        return <_AnonymousSFLButton {...props} />;
    }
}

export default SaveForLaterButton;
